import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { audioTranscriptionService as audioService } from '@lib/audioTranscriptionService';
import { useSessionAppointmentId } from './useSessionAppointmentId';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import { useQuery } from '@tanstack/react-query';
import { useHeidiSession } from '@components/Clinicians/Session/hooks/useHeidiSession';
import { useStoreIndexedDB } from '@hooks/useStoreIndexedDB';

type LatestChunkType = {
  blob: Blob;
  id: number;
  filename: string;
} | null;

type RetryOptions<T> = {
  isSuccess?: (result: T) => boolean;
  shouldRetry?: (error: unknown, attempt: number) => boolean;
};

const createPayload = (
  latestChunk: NonNullable<LatestChunkType>,
  apptId: string
): Promise<{ chunk_base64: string; appointment_id: string }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(latestChunk.blob);

    reader.onloadend = () => {
      try {
        const base64Data = (reader.result as string).split(',')[1];

        resolve({
          chunk_base64: base64Data,
          appointment_id: apptId
        });
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
  });
};

const withRetry = async <T>(fn: () => Promise<T>, options: RetryOptions<T>): Promise<T> => {
  const { isSuccess = () => true, shouldRetry = () => true } = options;

  const MAX_RETRIES = 3;
  const DELAY = 1000;
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const result = await fn();

      if (isSuccess(result)) {
        return result;
      }

      if (attempt === MAX_RETRIES) {
        console.error('Unable to store audio chunk');
      }
    } catch (error) {
      if (!shouldRetry(error, attempt) || attempt === MAX_RETRIES) {
        throw error;
      }
      await new Promise((res) => setTimeout(res, DELAY));
    }
  }
  throw new Error('Unexpected retry failure');
};

const uploadAudioTranscriptionFiles = async (
  dispatch: any,
  latestChunk: LatestChunkType,
  apptId: number,
  saveChunkToDisk: (blob: Blob, chunkIndex: number, mimeType: string) => Promise<void>,
  markAsSynced: (key: string) => Promise<void>,
  setIsChunkStoreError: (value: boolean) => void,
  setIsChunkUploadSuccess: Dispatch<SetStateAction<{ isIndexedDB: boolean; isBackend: boolean }>>
) => {
  if (!latestChunk || !apptId) return;

  let isChunkStoredInDB = false;
  let isChunkStoredInBE = false;

  const latestChunkName = `${apptId}:${latestChunk.filename}:${latestChunk.id}`;
  const payload = await createPayload(latestChunk, String(apptId));

  try {
    const responseDB = await withRetry<{ responseDBSuccess: boolean }>(
      async () => {
        try {
          await saveChunkToDisk(latestChunk.blob, latestChunk.id, 'audio/wav');
          return { responseDBSuccess: true };
        } catch (err) {
          console.log('Error:', err);
          return { responseDBSuccess: false };
        }
      },
      {
        isSuccess: (res) => res.responseDBSuccess
      }
    );
    if (responseDB.responseDBSuccess) {
      isChunkStoredInDB = true;
    }
  } catch {
    console.error('Unable to save chunk to Indexed DB');
    setIsChunkStoreError(true);
  } finally {
    setIsChunkUploadSuccess((prev) => ({ ...prev, isIndexedDB: isChunkStoredInDB }));
  }

  if (isChunkStoredInDB) {
    try {
      const responseBE = await withRetry<{ isSuccess: boolean }>(
        () => dispatch(actions.uploadTranscriptionAudio(payload, apptId)),
        {
          isSuccess: (res) => res.isSuccess
        }
      );
      if (responseBE.isSuccess) {
        isChunkStoredInBE = true;
        await markAsSynced(latestChunkName).catch((error) => {
          console.error(`Unable to remove chunk from Indexed DB: ${error}`);
        });
      }
    } catch {
      console.error('Unable to send chunk to BE or service');
      setIsChunkStoreError(true);
      await markAsSynced(latestChunkName).catch((error) => {
        console.error(`Unable to remove chunk from Indexed DB: ${error}`);
      });
    } finally {
      setIsChunkUploadSuccess((prev) => ({ ...prev, isBackend: isChunkStoredInBE }));
    }
  }
};

const useAINotesChunkSize = () => {
  const appt_id = useSessionAppointmentId();
  const dispatch = useDispatch();

  const { data, isLoading } = useQuery({
    queryKey: ['getAINotesChunkSize', appt_id],
    queryFn: () => dispatch(actions.getAIMeta(appt_id)),
    enabled: !!appt_id
  });

  if (isLoading) return null;

  return data?.ai_notes_meta?.[0]?.chunk_size ?? 25;
};

export const useAudioTranscription = () => {
  const appt_id = useSessionAppointmentId();
  const chunk_size = useAINotesChunkSize();
  const dispatch = useDispatch();
  const { status, pause } = useHeidiSession();

  const [isRecording, setIsRecording] = useState(audioService.isRecording);
  const [isAudioError, setIsAudioError] = useState(audioService.isAudioError);
  const [isRecordingPaused, setIsRecordingPaused] = useState(audioService.isRecordingPaused);
  const [latestChunk, setLatestChunk] = useState<LatestChunkType>(null);
  const [isChunkStoreError, setIsChunkStoreError] = useState(false);
  const [isChunkUploadSuccess, setIsChunkUploadSuccess] = useState({
    isIndexedDB: false,
    isBackend: false
  });
  const { saveChunkToDisk, markAsSynced, purgeSession } = useStoreIndexedDB({
    appointmentId: appt_id,
    recordingId: latestChunk?.filename
  });

  useEffect(() => {
    if (!latestChunk) return;
    uploadAudioTranscriptionFiles(
      dispatch,
      latestChunk,
      appt_id,
      saveChunkToDisk,
      markAsSynced,
      setIsChunkStoreError,
      setIsChunkUploadSuccess
    );
  }, [latestChunk, dispatch, setIsChunkStoreError, setIsChunkUploadSuccess]);

  useEffect(() => {
    if (audioService.isAudioError && status === 'active') {
      pause();
    }
  }, [status, pause, isAudioError]);

  useEffect(() => {
    if (chunk_size === null) return;
    switch (status) {
      case 'active':
        if (!audioService.isRecording) {
          console.log(`Starting transcription with ${Number(chunk_size)}s chunks`);
          console.log('Audio Capture Active...');
          audioService.setAudioError(false);
          audioService.setIsRecordingPaused(false);
          audioService.start(Number(chunk_size));
        }
        break;

      case 'paused':
        if (audioService.isRecording) {
          console.log('Audio Capture Paused...');
          audioService.setIsRecordingPaused(true);
          audioService.stop();
        }
        break;

      case 'processing':
        if (audioService.isRecording || audioService.isRecordingPaused) {
          audioService.stop();
        }
        break;

      case 'completed':
        if (!audioService.isRecording) {
          purgeSession();
        }
        break;

      case 'idle':
      default:
        if (audioService.isRecording) {
          audioService.stop();
        }
        break;
    }
  }, [isRecording, isRecordingPaused, status, chunk_size]);

  useEffect(() => {
    const onStateChange = (state: boolean) => setIsRecording(state);
    const onError = (state: boolean) => setIsAudioError(state);
    const onRecordingPausedChange = (state: boolean) => setIsRecordingPaused(state);

    const onChunk = (blob: Blob) => {
      setLatestChunk({ blob, id: Date.now(), filename: `${appt_id}-${Date.now()}` });
    };

    audioService.subscribe('isRecording', onStateChange);
    audioService.subscribe('isAudioError', onError);
    audioService.subscribe('isRecordingPaused', onRecordingPausedChange);
    audioService.subscribe('chunk', onChunk);

    return () => {
      audioService.stop();

      audioService.unsubscribe('isRecording', onStateChange);
      audioService.unsubscribe('isAudioError', onError);
      audioService.unsubscribe('isRecordingPaused', onRecordingPausedChange);
      audioService.unsubscribe('chunk', onChunk);
    };
  }, [appt_id]);

  return {
    isChunkUploadSuccess,
    isRecording,
    isAudioError,
    isRecordingPaused,
    isChunkStoreError,
    latestChunk,
    status
  };
};
